import { t } from "@/lib/i18n";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check, Wand2 } from "lucide-react";
import { cn, Input, Label } from "@fox1ck-org/ui";
import { EntityDrawer } from "@fox1ck-org/ui";
import { LanderPicker } from "@/components/canonical/pickers/lander-picker";
import { OfferPicker } from "@/components/canonical/pickers/offer-picker";
import { paramKindLabel } from "@/lib/services/flow-template-service";
import {
  TemplateParameterKind,
  type FlowTemplate,
  type TemplateParameter,
} from "@gen/tracker/v1/flow_template_pb";
import { MiniBlueprintPreview } from "./mini-preview";
import { buildBindingsSchema } from "@/lib/schemas/flow-template";

export interface ApplyTemplateWizardProps {
  open: boolean;
  template: FlowTemplate | null;
  onOpenChange: (open: boolean) => void;
  onApply: (bindings: Record<string, string>) => Promise<void> | void;
  isApplying?: boolean;
}

export function ApplyTemplateWizard({
  open,
  template,
  onOpenChange,
  onApply,
  isApplying,
}: ApplyTemplateWizardProps) {
  const params = template?.parameters ?? [];
  const [bindings, setBindings] = useState<Record<string, string>>({});
  const [stepIdx, setStepIdx] = useState(0);

  // Reset state when the dialog opens for a new template.
  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setBindings(
      Object.fromEntries((template?.parameters ?? []).map((p) => [p.key, p.defaultValue ?? ""])),
    );
  }, [open, template]);

  const currentParam: TemplateParameter | undefined = params[stepIdx];
  const isLastStep = stepIdx >= params.length - 1;
  const isReviewStep = stepIdx >= params.length;
  const totalSteps = Math.max(1, params.length);

  const stepValid = useMemo(() => {
    if (!currentParam) return true;
    return !!(bindings[currentParam.key]?.trim() || currentParam.defaultValue?.trim());
  }, [bindings, currentParam]);

  const allValid = useMemo(() => {
    return params.every(
      (p) => !!(bindings[p.key]?.trim() || p.defaultValue?.trim()),
    );
  }, [bindings, params]);

  function next() {
    if (isReviewStep) return;
    setStepIdx((i) => Math.min(i + 1, params.length));
  }

  function prev() {
    setStepIdx((i) => Math.max(i - 1, 0));
  }

  async function handleApply() {
    if (!allValid) return;
    const draft: Record<string, string> = {};
    for (const p of params) {
      draft[p.key] = bindings[p.key]?.trim() || p.defaultValue || "";
    }
    // Final zod parse — UUIDs for Lander/Offer/Network bindings must look
    // valid before we round-trip InstantiateFlowTemplate. Server still
    // validates existence, but failing here keeps the error inline and avoids
    // a wasted RPC.
    const schema = buildBindingsSchema(
      params.map((p) => ({
        key: p.key,
        kind: p.kind,
        defaultValue: p.defaultValue,
      })),
    );
    const parsed = schema.safeParse(draft);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const path = issue?.path?.[0];
      const fieldName = path != null ? String(path) : "binding";
      throw new Error(`${fieldName}: ${issue?.message ?? "invalid"}`);
    }
    await onApply(parsed.data as Record<string, string>);
  }

  if (!template) {
    return null;
  }

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      width="2xl"
      title={template.name}
      description={template.description}
      primaryAction={
        isReviewStep
          ? {
              label: isApplying ? t("aw.creating") : t("aw.create"),
              onClick: handleApply,
              loading: isApplying,
              disabled: !allValid,
            }
          : {
              label: isLastStep ? t("aw.review") : t("aw.next"),
              onClick: next,
              disabled: !stepValid,
            }
      }
      secondaryAction={
        stepIdx > 0
          ? { label: t("aw.back"), onClick: prev }
          : { label: t("rule.ed.cancel"), onClick: () => onOpenChange(false) }
      }
    >
      <div className="space-y-5">
        <Stepper params={params} stepIdx={stepIdx} />

        <Preview
          template={template}
          bindings={bindings}
          highlightKey={currentParam?.key}
        />

        {isReviewStep ? (
          <ReviewStep params={params} bindings={bindings} />
        ) : currentParam ? (
          <ParamStep
            param={currentParam}
            value={bindings[currentParam.key] ?? ""}
            onChange={(v) =>
              setBindings((s) => ({ ...s, [currentParam.key]: v }))
            }
            stepIdx={stepIdx}
            totalSteps={totalSteps}
          />
        ) : (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-6 text-center">
            <Wand2 className="mx-auto h-5 w-5 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-semibold text-foreground">
              {t("aw.noParams")}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("aw.noParams.hint")}
            </p>
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function Stepper({
  params,
  stepIdx,
}: {
  params: TemplateParameter[];
  stepIdx: number;
}) {
  if (params.length === 0) return null;
  const reviewIdx = params.length;
  const items = [...params, "review" as const];
  return (
    <ol className="flex items-center gap-2 overflow-x-auto pb-1">
      {items.map((item, i) => {
        const active = i === stepIdx;
        const done = i < stepIdx;
        const label = item === "review" ? t("aw.review") : item.label || item.key;
        return (
          <li key={i} className="flex items-center gap-2">
            <div
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors",
                done
                  ? "bg-primary text-primary-foreground"
                  : active
                    ? "border-2 border-primary text-primary"
                    : "border border-border text-muted-foreground",
              )}
            >
              {done ? <Check className="h-3 w-3" /> : i + 1}
            </div>
            <span
              className={cn(
                "whitespace-nowrap text-xs",
                active
                  ? "font-medium text-foreground"
                  : done
                    ? "text-muted-foreground"
                    : "text-muted-foreground/70",
              )}
            >
              {label}
            </span>
            {i < items.length - 1 ? (
              <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/40" />
            ) : null}
            {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
            {reviewIdx ? null : null}
          </li>
        );
      })}
    </ol>
  );
}

function Preview({
  template,
  bindings,
  highlightKey,
}: {
  template: FlowTemplate;
  bindings: Record<string, string>;
  highlightKey?: string;
}) {
  const blueprintNodes = template.templateBlueprint?.nodes ?? [];
  return (
    <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {t("aw.blueprint")}
          </div>
          <p className="text-xs text-foreground">
            {template.templateBlueprint?.name ?? template.name}
          </p>
        </div>
        <span className="rounded-full border border-border bg-card px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
          {t("aw.nodes", { n: blueprintNodes.length })}
        </span>
      </div>

      <div className="rounded-md border border-border bg-card p-2">
        <MiniBlueprintPreview
          nodes={blueprintNodes}
          edges={template.templateBlueprint?.edges ?? []}
          defaultNodeId={template.templateBlueprint?.defaultNodeId}
        />
      </div>

      {template.parameters.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {template.parameters.map((p) => {
            const v = bindings[p.key]?.trim();
            const filled = !!(v || p.defaultValue?.trim());
            return (
              <span
                key={p.key}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors",
                  highlightKey === p.key
                    ? "border-primary bg-primary/10 text-primary"
                    : filled
                      ? "border-ok/40 bg-ok/5 text-ok"
                      : "border-border bg-muted/40 text-muted-foreground",
                )}
              >
                <code className="font-mono text-xs">{`\${${p.key}}`}</code>
                {filled ? <Check className="h-3 w-3" /> : null}
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ParamStep({
  param,
  value,
  onChange,
  stepIdx,
  totalSteps,
}: {
  param: TemplateParameter;
  value: string;
  onChange: (v: string) => void;
  stepIdx: number;
  totalSteps: number;
}) {
  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t("aw.step", { i: stepIdx + 1, n: totalSteps + 1 })}
        </div>
        <h2 className="mt-1 text-md font-semibold text-foreground">
          {param.label || param.key}
        </h2>
        {param.description ? (
          <p className="mt-1 text-xs text-muted-foreground">{param.description}</p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label textCase="none" className="text-xs font-medium text-foreground">
          {kindLabel(param.kind)}
        </Label>
        {param.kind === TemplateParameterKind.LANDER ? (
          <LanderPicker value={value} onChange={onChange} />
        ) : param.kind === TemplateParameterKind.OFFER ? (
          <OfferPicker value={value} onChange={onChange} />
        ) : param.kind === TemplateParameterKind.COUNTRY ? (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            placeholder={param.defaultValue || "US"}
            maxLength={2}
            className="w-32 font-mono uppercase"
          />
        ) : param.kind === TemplateParameterKind.NUMBER ? (
          <Input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={param.defaultValue || "0"}
            className="w-32"
          />
        ) : (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={param.defaultValue || "—"}
          />
        )}
        {param.defaultValue ? (
          <p className="text-xs text-muted-foreground">
            {t("aw.default")} <code className="rounded bg-muted px-1 py-0.5">{param.defaultValue}</code>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ReviewStep({
  params,
  bindings,
}: {
  params: TemplateParameter[];
  bindings: Record<string, string>;
}) {
  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t("aw.review")}
        </div>
        <h2 className="mt-1 text-md font-semibold text-foreground">
          {t("aw.ready")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("aw.ready.hint")}
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-border">
        {params.map((p, i) => (
          <div
            key={p.key}
            className={cn(
              "grid grid-cols-3 items-center gap-2 px-3 py-2 text-xs",
              i % 2 ? "bg-muted/40" : "bg-card",
            )}
          >
            <code className="truncate font-mono text-muted-foreground">{`\${${p.key}}`}</code>
            <span className="text-foreground">{p.label || kindLabel(p.kind)}</span>
            <span className="truncate text-foreground">
              {bindings[p.key] || p.defaultValue || (
                <span className="text-destructive">{t("aw.unbound")}</span>
              )}
            </span>
          </div>
        ))}
        {params.length === 0 ? (
          <div className="px-3 py-3 text-xs text-muted-foreground">
            {t("aw.noParams.review")}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function kindLabel(k: TemplateParameterKind): string {
  return paramKindLabel(k);
}
